import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2, PenLine, Send, CheckCircle2, XCircle, FileDown, Check, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import SignaturePad from '../ui/SignaturePad';
import { useAuth } from '../../contexts/AuthContext';
import { isApprover, STATUS_TONE } from './MileageSheets';
import { SaveRouteTemplate, AddRouteTemplate } from './RouteTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EDITABLE = ['draft', 'rejected'];
const rid = () => `r-${Math.random().toString(36).slice(2, 10)}`;
const monthBounds = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return { min: `${m}-01`, max: `${m}-${String(new Date(y, mo, 0).getDate()).padStart(2, '0')}` };
};

const shiftDate = (date, days) => {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Copy of a trip for the next day (kept inside the sheet's month), unsigned and with its odometer recalculated.
const nextDayCopy = (row, bounds) => {
  let date = row.date || '';
  if (date) {
    const next = shiftDate(date, 1);
    date = next > bounds.max ? bounds.max : next;
  }
  return { ...row, id: rid(), date, odometer: '', signature_id: null, signed_at: null };
};

// Selected trips copied to the same weekday next week, placed after the last selected trip; copies past month end are skipped.
const nextWeekCopies = (rows, ids, bounds) => {
  const picked = rows.filter((r) => ids.includes(r.id));
  const copies = picked.filter((r) => !r.date || shiftDate(r.date, 7) <= bounds.max)
    .map((r) => ({ ...r, id: rid(), date: r.date ? shiftDate(r.date, 7) : '', odometer: '', signature_id: null, signed_at: null }));
  const at = Math.max(...picked.map((r) => rows.indexOf(r))) + 1;
  return { rows: [...rows.slice(0, at), ...copies, ...rows.slice(at)], added: copies.length, skipped: picked.length - copies.length };
};

// Empty odometer readings follow on from the previous reading plus the trip's km.
const fillOdometers = (rows, start) => {
  let last = Number(start) || 0;
  return rows.map((r) => {
    const odo = r.odometer === '' || r.odometer == null ? (last ? Math.round((last + Number(r.km || 0)) * 10) / 10 : null) : Number(r.odometer);
    if (odo != null) last = odo;
    return { ...r, odometer: odo };
  });
};

const SignDialog = ({ open, title, onClose, onSign, extra }) => {
  const { t } = useTranslation();
  const [sig, setSig] = useState(null);
  useEffect(() => { if (open) setSig(null); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="mileage-sign-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {extra}
        <SignaturePad value={sig} onChange={setSig} height={140} testIdPrefix="mileage-sig" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!sig} onClick={() => onSign(sig)} data-testid="mileage-sign-confirm">{t('mileage.signConfirm')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RowEditor = ({ row, i, editable, bounds, selected, onSelect, onChange, onRemove, onDuplicate, driverName }) => {
  const { t } = useTranslation();
  const set = (k, v) => onChange({ ...row, [k]: v });
  const cell = 'h-8 text-xs px-2';
  return (
    <tr className="border-b align-middle" data-testid={`mileage-trip-${i}`}>
      <td className="px-1">{editable && <Checkbox checked={selected} onCheckedChange={(v) => onSelect(!!v)} data-testid={`mileage-trip-select-${i}`} />}</td>
      <td className="px-1 text-xs text-slate-500">{i + 1}</td>
      <td className="px-1"><Input type="date" className={`${cell} w-40`} min={bounds.min} max={bounds.max} value={row.date || ''} disabled={!editable} onChange={(e) => set('date', e.target.value)} data-testid={`mileage-trip-date-${i}`} /></td>
      <td className="px-1"><Input className={`${cell} min-w-[10rem]`} value={row.purpose || ''} disabled={!editable} onChange={(e) => set('purpose', e.target.value)} data-testid={`mileage-trip-purpose-${i}`} /></td>
      <td className="px-1"><Input className={`${cell} min-w-[7rem]`} value={row.origin || ''} disabled={!editable} onChange={(e) => set('origin', e.target.value)} data-testid={`mileage-trip-origin-${i}`} /></td>
      <td className="px-1"><Input className={`${cell} min-w-[7rem]`} value={row.destination || ''} disabled={!editable} onChange={(e) => set('destination', e.target.value)} data-testid={`mileage-trip-destination-${i}`} /></td>
      <td className="px-1"><Input type="number" step="0.1" min="0" className={`${cell} w-20`} value={row.km ?? ''} disabled={!editable} onChange={(e) => set('km', e.target.value)} data-testid={`mileage-trip-km-${i}`} /></td>
      <td className="px-1"><Input type="number" step="0.1" className={`${cell} w-24`} value={row.odometer ?? ''} disabled={!editable} onChange={(e) => set('odometer', e.target.value)} data-testid={`mileage-trip-odometer-${i}`} /></td>
      <td className="px-1"><Input className={`${cell} w-32`} placeholder={driverName} value={row.driver || ''} disabled={!editable} onChange={(e) => set('driver', e.target.value)} data-testid={`mileage-trip-driver-${i}`} /></td>
      <td className="px-1 text-center">{row.signature_id ? <Check className="mx-auto h-4 w-4 text-emerald-600" data-testid={`mileage-trip-signed-${i}`} /> : <span className="text-[11px] text-slate-400">—</span>}</td>
      <td className="px-1 whitespace-nowrap">{editable && (
        <>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-600" title={t('mileage.copyRow')} aria-label={t('mileage.copyRow')} onClick={onDuplicate} data-testid={`mileage-trip-duplicate-${i}`}><Copy className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={onRemove} data-testid={`mileage-trip-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </>
      )}</td>
    </tr>
  );
};

const RateBar = ({ sheet, setSheet, rates, editable }) => {
  const { t } = useTranslation();
  return (
    <div className="grid gap-2 sm:grid-cols-4">
      <Select value={sheet.rate_mode} onValueChange={(v) => setSheet({ ...sheet, rate_mode: v })} disabled={!editable}>
        <SelectTrigger className="h-9 text-xs" data-testid="mileage-rate-mode"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="statutory">{t('mileage.rateStatutory')}</SelectItem>
          <SelectItem value="custom">{t('mileage.rateCustom')}</SelectItem>
        </SelectContent>
      </Select>
      {sheet.rate_mode === 'statutory' ? (
        <Select value={sheet.rate_key || undefined} onValueChange={(v) => setSheet({ ...sheet, rate_key: v })} disabled={!editable}>
          <SelectTrigger className="h-9 text-xs sm:col-span-2" data-testid="mileage-rate-key"><SelectValue placeholder={t('mileage.pickRate')} /></SelectTrigger>
          <SelectContent>{rates.map((r) => <SelectItem key={r.key} value={r.key}>{r.label} — {String(r.rate).replace('.', ',')} zł/km</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input type="number" step="0.01" min="0" className="h-9 text-xs sm:col-span-2" placeholder={t('mileage.ownRate')} value={sheet.rate_per_km ?? ''} disabled={!editable}
          onChange={(e) => setSheet({ ...sheet, rate_per_km: e.target.value })} data-testid="mileage-rate-custom" />
      )}
      <Input type="number" step="0.1" className="h-9 text-xs" placeholder={t('mileage.startOdometer')} value={sheet.start_odometer ?? ''} disabled={!editable}
        onChange={(e) => setSheet({ ...sheet, start_odometer: e.target.value })} data-testid="mileage-start-odometer" />
    </div>
  );
};

/** Fill, sign, submit and sign off one monthly mileage sheet. */
export const MileageSheetEditor = ({ sheetId, onClose, onChanged }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [rates, setRates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [signing, setSigning] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const [s, c] = await Promise.all([axios.get(`${API}/mileage-sheets/${sheetId}`), axios.get(`${API}/mileage-sheets/config`)]);
    setSheet(s.data); setRates(c.data.rates); setSelected([]);
  }, [sheetId]);
  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || t('mileage.failed'))); }, [load, t]);
  if (!sheet) return null;
  const editable = EDITABLE.includes(sheet.status);
  const bounds = monthBounds(sheet.month);
  const totalKm = (sheet.rows || []).reduce((s, r) => s + (Number(r.km) || 0), 0);
  const rate = sheet.rate_mode === 'statutory' ? (rates.find((r) => r.key === sheet.rate_key)?.rate || 0) : Number(sheet.rate_per_km) || 0;
  const setRows = (rows) => setSheet({ ...sheet, rows });

  const call = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data) { setSheet(r.data); setSelected([]); setReason(''); onChanged?.(); } if (ok) toast.success(ok); return true; }
    catch (e) { toast.error(e.response?.data?.detail || t('mileage.failed')); return false; }
    finally { setBusy(false); }
  };
  const save = (ok = t('mileage.saved')) => call(() => axios.put(`${API}/mileage-sheets/${sheet.id}`, {
    rows: fillOdometers(sheet.rows || [], sheet.start_odometer), rate_mode: sheet.rate_mode, rate_key: sheet.rate_key,
    rate_per_km: sheet.rate_per_km === '' ? null : Number(sheet.rate_per_km),
    start_odometer: sheet.start_odometer === '' || sheet.start_odometer == null ? null : Number(sheet.start_odometer),
  }), ok);
  const sign = async (sig) => {
    const ids = signing.ids;
    setSigning(null);
    if (!(await save(null))) return;
    await call(() => axios.post(`${API}/mileage-sheets/${sheet.id}/sign-rows`, { row_ids: ids, signature: sig }), t('mileage.signedRows', { count: ids.length }));
  };
  const approve = (sig) => { setSigning(null); call(() => axios.post(`${API}/mileage-sheets/${sheet.id}/approve`, { signature: sig }), t('mileage.approvedToast')); };
  const submit = async () => { if (await save(null)) call(() => axios.post(`${API}/mileage-sheets/${sheet.id}/submit`), t('mileage.submittedToast')); };
  const pdf = async () => {
    const r = await axios.get(`${API}/mileage-sheets/${sheet.id}/pdf`, { responseType: 'blob' });
    const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'mileage.pdf';
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement('a'), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  };
  const unsignedIds = (sheet.rows || []).filter((r) => !r.signature_id).map((r) => r.id);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="mileage-editor">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {t('mileage.sheetTitle', { month: sheet.month })} · {sheet.asset_name} {sheet.registration}
            <Badge className={STATUS_TONE[sheet.status]} data-testid="mileage-status">{t(`mileage.status.${sheet.status}`)}</Badge>
          </DialogTitle>
          <p className="text-xs text-slate-500">{sheet.driver_name} · {sheet.project_name}</p>
        </DialogHeader>
        {sheet.reopened && sheet.status === 'draft' && sheet.reopen_reason && <p className="rounded bg-amber-50 p-2 text-xs text-amber-800" data-testid="mileage-reopened">{t('mileage.reopenedNote')}: {sheet.reopen_reason}</p>}
        {sheet.status === 'rejected' && sheet.rejection_reason && <p className="rounded bg-red-50 p-2 text-xs text-red-700" data-testid="mileage-rejection">{t('mileage.sentBack')}: {sheet.rejection_reason}</p>}
        {sheet.approval && <p className="rounded bg-emerald-50 p-2 text-xs text-emerald-800" data-testid="mileage-approval">{t('mileage.approvedBy', { name: sheet.approval.name, date: String(sheet.approval.at).slice(0, 10) })}</p>}
        <RateBar sheet={sheet} setSheet={setSheet} rates={rates} editable={editable} />
        <div className="overflow-x-auto rounded border">
          <table className="w-full min-w-[900px] text-left">
            <thead className="bg-slate-50 text-[11px] text-slate-600">
              <tr>
                <th className="px-1 py-2">{editable && <Checkbox checked={selected.length > 0 && selected.length === (sheet.rows || []).length} onCheckedChange={(v) => setSelected(v ? sheet.rows.map((r) => r.id) : [])} data-testid="mileage-select-all" />}</th>
                <th className="px-1">#</th><th className="px-1">{t('mileage.cols.date')}</th><th className="px-1">{t('mileage.cols.purpose')}</th>
                <th className="px-1">{t('mileage.cols.origin')}</th><th className="px-1">{t('mileage.cols.destination')}</th><th className="px-1">km</th>
                <th className="px-1">{t('mileage.cols.odometer')}</th><th className="px-1">{t('mileage.cols.driver')}</th><th className="px-1">{t('mileage.cols.signed')}</th><th />
              </tr>
            </thead>
            <tbody>
              {(sheet.rows || []).map((r, i) => (
                <RowEditor key={r.id} row={r} i={i} editable={editable} bounds={bounds} driverName={sheet.driver_name}
                  selected={selected.includes(r.id)} onSelect={(v) => setSelected((p) => (v ? [...p, r.id] : p.filter((x) => x !== r.id)))}
                  onChange={(nr) => setRows(sheet.rows.map((x) => (x.id === r.id ? { ...nr, signature_id: null } : x)))}
                  onRemove={() => setRows(sheet.rows.filter((x) => x.id !== r.id))}
                  onDuplicate={() => setRows([...sheet.rows.slice(0, i + 1), nextDayCopy(r, bounds), ...sheet.rows.slice(i + 1)])} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          {editable ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setRows([...(sheet.rows || []), { id: rid(), date: '', purpose: '', origin: sheet.rows?.at(-1)?.destination || '', destination: '', km: '', odometer: '', driver: '' }])} data-testid="mileage-add-trip"><Plus className="h-4 w-4 mr-1" />{t('mileage.addTrip')}</Button>
              <Button size="sm" variant="outline" disabled={!(sheet.rows || []).length}
                onClick={() => setRows(fillOdometers((sheet.rows || []).map((r) => ({ ...r, odometer: '', signature_id: r.signature_id && null })), sheet.start_odometer))}
                data-testid="mileage-recalc-odometers">{t('mileage.recalc')}</Button>
              <Button size="sm" variant="outline" disabled={!selected.length || busy} onClick={() => {
                const res = nextWeekCopies(sheet.rows || [], selected, bounds);
                setRows(res.rows); setSelected([]);
                if (res.added) toast.success(t('mileage.copiedNextWeek', { count: res.added }));
                if (res.skipped) toast.warning(t('mileage.copySkipped', { count: res.skipped }));
              }} data-testid="mileage-copy-next-week"><Copy className="h-4 w-4 mr-1" />{t('mileage.copyNextWeek', { count: selected.length })}</Button>
              <SaveRouteTemplate rows={sheet.rows || []} selected={selected} disabled={busy} onSaved={() => setSelected([])} />
              <AddRouteTemplate month={sheet.month} disabled={busy} onAdd={(added) => setRows([...(sheet.rows || []), ...added.map((r) => ({ ...r, id: rid() }))]
                .map((r, i) => [r, i]).sort((a, b) => (a[0].date || '9999').localeCompare(b[0].date || '9999') || a[1] - b[1]).map(([r]) => r))} />
              <Button size="sm" variant="outline" disabled={!selected.length || busy} onClick={() => setSigning({ ids: selected, mode: 'rows' })} data-testid="mileage-sign-selected"><PenLine className="h-4 w-4 mr-1" />{t('mileage.signSelected', { count: selected.length })}</Button>
              <Button size="sm" variant="outline" disabled={!unsignedIds.length || busy} onClick={() => setSigning({ ids: unsignedIds, mode: 'rows' })} data-testid="mileage-sign-all"><PenLine className="h-4 w-4 mr-1" />{t('mileage.signAll', { count: unsignedIds.length })}</Button>
            </div>
          ) : <span />}
          <span className="font-medium" data-testid="mileage-totals">{t('mileage.totals', { km: totalKm.toFixed(1).replace('.', ','), amount: (totalKm * rate).toFixed(2).replace('.', ',') })}</span>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={pdf} data-testid="mileage-pdf"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
          {editable && <Button variant="outline" onClick={() => save()} disabled={busy} data-testid="mileage-save">{t('common.save')}</Button>}
          {editable && <Button onClick={submit} disabled={busy} data-testid="mileage-submit"><Send className="h-4 w-4 mr-1" />{t('mileage.submit')}</Button>}
          {(user?.role === 'admin' || user?.is_superadmin) && sheet.status === 'approved' && (
            <Button variant="outline" onClick={() => setRejectOpen('reopen')} data-testid="mileage-reopen"><XCircle className="h-4 w-4 mr-1" />{t('mileage.reopen')}</Button>
          )}
          {isApprover(user) && sheet.status === 'submitted' && (
            <>
              <Button variant="outline" className="text-red-600" onClick={() => setRejectOpen(true)} data-testid="mileage-reject"><XCircle className="h-4 w-4 mr-1" />{t('mileage.sendBack')}</Button>
              <Button onClick={() => setSigning({ mode: 'approve' })} data-testid="mileage-approve"><CheckCircle2 className="h-4 w-4 mr-1" />{t('mileage.signOff')}</Button>
            </>
          )}
        </DialogFooter>
        <SignDialog open={!!signing} onClose={() => setSigning(null)}
          title={signing?.mode === 'approve' ? t('mileage.signOffTitle', { amount: (totalKm * rate).toFixed(2).replace('.', ',') }) : t('mileage.signRowsTitle', { count: signing?.ids?.length || 0 })}
          onSign={signing?.mode === 'approve' ? approve : sign} />
        <Dialog open={!!rejectOpen} onOpenChange={(o) => !o && setRejectOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{rejectOpen === 'reopen' ? t('mileage.reopen') : t('mileage.sendBack')}</DialogTitle></DialogHeader>
            {rejectOpen === 'reopen' && <p className="text-sm text-slate-600">{t('mileage.reopenHint')}</p>}
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('mileage.reason')} data-testid="mileage-reject-reason" />
            <DialogFooter>
              <Button disabled={!reason.trim()} onClick={() => {
                const reopen = rejectOpen === 'reopen';
                setRejectOpen(false);
                call(() => axios.post(`${API}/mileage-sheets/${sheet.id}/${reopen ? 'reopen' : 'reject'}`, { reason }),
                  reopen ? t('mileage.reopenedToast') : t('mileage.sentBackToast'));
              }} data-testid="mileage-reject-confirm">{rejectOpen === 'reopen' ? t('mileage.reopen') : t('mileage.sendBack')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default MileageSheetEditor;
